/**
 * Lets Quill's plain buttons take part in the row's overflow - and nothing else.
 *
 * Quill binds handlers straight onto its elements and holds direct element
 * references for active-state updates, so a control keeps working when it moves.
 * But a `.ql-picker` owns the dropdown nested inside it, and relocating one into
 * a popover is what broke the editor toolbar before. So pickers are reported as
 * `movable: false` and are never touched; only the simple toggle buttons travel.
 *
 * Each button leaves a comment node behind when it moves, so it returns to its
 * exact original slot regardless of what its siblings did meanwhile.
 */

/** Class fragments that describe styling or state rather than a format. */
const NON_FORMAT = new Set([
  'picker', 'picker-label', 'picker-options', 'picker-item', 'formats', 'toolbar',
  'snow', 'expanded', 'active', 'selected', 'flip', 'stroke', 'fill', 'thin', 'empty',
]);

const PRIORITY = {
  'ql-bold': 99, 'ql-italic': 98, 'ql-underline': 97,
  'ql-list-bullet': 82, 'ql-list-ordered': 81,
  'ql-strike': 66, 'ql-link': 65, 'ql-code-block': 63,
  'ql-indent-+1': 50, 'ql-indent--1': 49,
  'ql-script-super': 30, 'ql-script-sub': 29,
  'ql-clean': 26, 'ql-direction-rtl': 20, 'ql-direction': 20,
};

const LABELS = {
  'ql-bold': 'Bold', 'ql-italic': 'Italic', 'ql-underline': 'Underline',
  'ql-strike': 'Strikethrough', 'ql-link': 'Link', 'ql-code-block': 'Code block',
  'ql-list-ordered': 'Numbered list', 'ql-list-bullet': 'Bulleted list',
  'ql-script-super': 'Superscript', 'ql-script-sub': 'Subscript',
  'ql-indent-+1': 'Increase indent', 'ql-indent--1': 'Decrease indent',
  'ql-direction-rtl': 'Right to left', 'ql-direction': 'Right to left',
  'ql-clean': 'Clear formatting',
};

/** Four configs feed this container and they disagree; unknowns sit mid-table. */
export const DEFAULT_PRIORITY = 55;

const formatOf = (el) => {
  for (const cls of Array.from(el.classList || [])) {
    if (cls.indexOf('ql-') !== 0) continue;
    const name = cls.slice(3);
    if (!NON_FORMAT.has(name)) return name;
  }
  return null;
};

/** Stable id. list/script/indent/direction repeat, so `value` disambiguates. */
export function deriveId(el) {
  const format = formatOf(el);
  if (!format) return null;
  const value = typeof el.getAttribute === 'function' ? el.getAttribute('value') : null;
  return value ? `ql-${format}-${value}` : `ql-${format}`;
}

export const labelFor = (id) => LABELS[id] || id.replace(/^ql-/, '').replace(/-/g, ' ');
export const priorityFor = (id) => (PRIORITY[id] === undefined ? DEFAULT_PRIORITY : PRIORITY[id]);

/** Pickers own a nested dropdown, so they stay in the row permanently. */
export const isMovable = (el) => el.tagName === 'BUTTON';

export function createQuillControls({ host, onChange = () => {} }) {
  let records = [];
  let observer = null;
  let owner = null;

  const unpark = (rec) => {
    if (!rec.parked) return;
    if (rec.placeholder.parentNode) {
      rec.placeholder.parentNode.replaceChild(rec.node, rec.placeholder);
    }
    if (rec.row.parentNode) rec.row.remove();
    rec.parked = false;
  };

  const park = (rec, mount) => {
    if (!rec.parked) {
      if (rec.node.parentNode) rec.node.parentNode.insertBefore(rec.placeholder, rec.node);
      rec.row.insertBefore(rec.node, rec.row.firstChild);
      rec.parked = true;
    }
    mount.appendChild(rec.row);
  };

  const forget = (rec) => {
    unpark(rec);
    if (rec.placeholder.parentNode) rec.placeholder.remove();
    delete rec.node.dataset.oid;
  };

  /** A group whose buttons have all left must not leave its hairline behind. */
  const syncGroups = (toolbar) => {
    if (!toolbar) return;
    for (const group of Array.from(toolbar.querySelectorAll('.ql-formats'))) {
      const stillHere = group.querySelector('button, .ql-picker');
      if (stillHere) group.removeAttribute('data-ovm-empty');
      else group.setAttribute('data-ovm-empty', 'true');
    }
  };

  const scan = () => {
    const toolbar = host ? host.querySelector('.ql-toolbar') : null;
    const before = records.map((rec) => rec.id).join('|');

    if (toolbar !== owner) owner = toolbar;

    // A parked control is still connected, so "is it still ours?" has to be
    // asked of the toolbar that owned it, not of the control itself.
    const surviving = [];
    records.forEach((rec) => {
      if (toolbar && rec.owner === toolbar) surviving.push(rec);
      else forget(rec);
    });
    records = surviving;

    if (toolbar) {
      const known = new Set(records.map((rec) => rec.id));
      Array.from(toolbar.querySelectorAll('.ql-formats > *')).forEach((node) => {
        const id = deriveId(node);
        if (!id || known.has(id)) return;
        known.add(id);
        node.dataset.oid = id;

        const row = document.createElement('div');
        row.className = 'ovm-row';
        row.setAttribute('role', 'menuitem');
        const label = document.createElement('span');
        label.className = 'ovm-row-label';
        label.textContent = labelFor(id);
        row.appendChild(label);

        records.push({ id, node, row, owner: toolbar, parked: false, movable: isMovable(node), placeholder: document.createComment(id) });
      });
    }

    syncGroups(toolbar);
    if (records.map((rec) => rec.id).join('|') !== before) onChange();
  };

  return {
    start() {
      if (!host || observer) return;
      observer = new MutationObserver(scan);
      observer.observe(host, { childList: true });
      scan();
    },

    stop() {
      if (observer) observer.disconnect();
      observer = null;
      records.forEach(forget);
      records = [];
      owner = null;
    },

    /** Descriptors for the fit algorithm, in toolbar order. */
    getItems() {
      return records.map((rec) => ({
        id: rec.id,
        priority: priorityFor(rec.id),
        label: labelFor(rec.id),
        movable: rec.movable,
      }));
    },

    /** Put every control back in the row so all widths are measurable. */
    returnAll() {
      records.forEach(unpark);
      syncGroups(owner);
    },

    apply(overflowIds, mount) {
      const overflow = new Set(overflowIds);
      records.forEach((rec) => {
        if (rec.movable && overflow.has(rec.id) && mount) park(rec, mount);
        else unpark(rec);
      });
      syncGroups(owner);
    },

    /** Test seam. */
    _records: () => records,
  };
}
